import { formatDateLabel, formatNumberLabel, formatPercent, toArray, toNullableNumber, toStringValue } from '@/features/charting-next/adapters/chartingAdapterUtils';

export const EMPTY_METRIC_VALUE = '-';

export const CHARTING_IDENTITY_METRIC_FIELDS = new Set(['ticker', 'entry_type']);
export const CHARTING_FIXED_METRIC_FIELDS = new Set(['ticker', 'entry_type', 'instrument_number', 'in_price', 'out_price', 'support_price', 'support_date']);

const PERSISTABLE_METRIC_FIELDS = [
    'ticker',
    'entry_type',
    'instrument_number',
    'in_price',
    'out_price',
    'support_price',
    'support_date',
    'current_price',
    'is_ipo',
    'amount',
    'in_currency',
    'benefit',
    'insured_benefit',
    'tthh_profit',
    'entry_distance',
    'in_date',
    'cfd_margin',
    'open_fee',
    'resistance',
    'anual_var',
    'wave_number',
    'next_entry_lt',
    'first_trading_date',
    'notes',
    'risk_reward',
    'tick_size',
    'stop_tick_size',
    'risk',
    'user_platinum_compatibility'
];

const LEGACY_FIELD_ALIASES = {
    valor_actual: 'current_price',
    current: 'current_price',
    exchange_rate: 'in_currency',
    real_entry: 'in_price',
    entry_price: 'in_price',
    stop_price: 'out_price',
    soport: 'support_price',
    data_soport: 'support_date',
    tae_lt: 'tthh_profit',
    profitability: 'tthh_profit',
    tae: 'anual_var',
    num_ona_actual: 'wave_number',
    entrada_seg: 'next_entry_lt',
    fecha_primera_barra: 'first_trading_date',
    platinum_compatibility: 'user_platinum_compatibility'
};

export const DEFAULT_CHARTING_METRIC_PREFERENCE_STATE = {
    ticker: 1,
    entry_type: 1,
    instrument_number: 1,
    in_price: 1,
    out_price: 1,
    support_price: 1,
    support_date: 1,
    current_price: 0,
    is_ipo: 0,
    amount: 0,
    in_currency: 0,
    benefit: 0,
    insured_benefit: 0,
    tthh_profit: 0,
    entry_distance: 0,
    in_date: 0,
    cfd_margin: 0,
    open_fee: 0,
    resistance: 0,
    anual_var: 0,
    wave_number: 0,
    next_entry_lt: 0,
    first_trading_date: 0,
    notes: 0,
    risk_reward: 0,
    tick_size: 0,
    stop_tick_size: 0,
    risk: 0,
    user_platinum_compatibility: 0
};

const firstText = (...values) => {
    for (const value of values) {
        const text = toStringValue(value);

        if (text) {
            return text;
        }
    }

    return '';
};

const formatNumber = (value, digits = 2) => {
    return formatNumberLabel(value, { maximumFractionDigits: digits });
};

const formatMetricPercent = (value, digits = 2) => {
    const parsed = toNullableNumber(value);

    if (parsed === null) {
        return '';
    }

    return formatPercent(parsed, digits);
};

const rawValue = (stock, ...keys) => {
    const raw = stock?.raw || {};

    for (const key of keys) {
        if (raw[key] !== null && raw[key] !== undefined && raw[key] !== '') {
            return raw[key];
        }
    }

    return '';
};

const formatCompatibility = (stock) => {
    if (stock?.userCompatibilityLabel) {
        return `${stock.userCompatibilityLabel}%`;
    }

    const rawCompatibility = toNullableNumber(rawValue(stock, 'user_platinum_compatibility'));

    if (rawCompatibility === null) {
        return '';
    }

    return formatPercent(rawCompatibility * 100, 2);
};

const resolveResistanceLabel = (stock) => firstText(formatNumber(stock?.resistancePriceValue), formatNumber(rawValue(stock, 'resistance', 'r')), rawValue(stock, 'resistance', 'r'));

export const CHARTING_METRIC_CATALOG = [
    {
        field: 'instrument_number',
        label: 'Acciones',
        resolveValue: (stock) => firstText(stock?.instrumentNumberLabel, formatNumber(rawValue(stock, 'instrument_number')), rawValue(stock, 'instrument_number'))
    },
    {
        field: 'in_price',
        label: 'Entrada',
        resolveValue: (stock) => firstText(stock?.entryPriceLabel, rawValue(stock, 'real_entry', 'in_price', 'entry_price'))
    },
    {
        field: 'out_price',
        label: 'Stop',
        resolveValue: (stock) => firstText(stock?.stopPriceLabel, rawValue(stock, 'out_price', 'stop_price'))
    },
    {
        field: 'support_price',
        label: 'Soporte',
        resolveValue: (stock) => firstText(stock?.supportPriceLabel, rawValue(stock, 'soport', 'support_price'))
    },
    {
        field: 'support_date',
        label: 'Fecha sop.',
        resolveValue: (stock) => firstText(stock?.supportDateFormatted, stock?.supportDateLabel, formatDateLabel(rawValue(stock, 'data_soport')), rawValue(stock, 'data_soport'))
    },
    {
        field: 'current_price',
        label: 'Precio',
        resolveValue: (stock) => firstText(stock?.priceLabel, rawValue(stock, 'valor_actual', 'current_price', 'price'))
    },
    {
        field: 'amount',
        label: 'Montante',
        resolveValue: (stock) => firstText(stock?.amountLabel, formatNumber(rawValue(stock, 'amount')), rawValue(stock, 'amount'))
    },
    {
        field: 'in_currency',
        label: 'Divisa',
        resolveValue: (stock) => firstText(stock?.currencyQuoteLabel, stock?.currency, formatNumber(rawValue(stock, 'cotitzacio_divisa')), rawValue(stock, 'cotitzacio_divisa', 'divisa'))
    },
    {
        field: 'tthh_profit',
        label: 'Rentab.',
        resolveValue: (stock) => firstText(stock?.profitabilityLabel, formatMetricPercent(rawValue(stock, 'tae_lt', 'profitability_interval'), 2))
    },
    {
        field: 'entry_distance',
        label: 'Dist. ent.',
        resolveValue: (stock) => firstText(stock?.entryDistanceLabel, formatMetricPercent(rawValue(stock, 'entry_distance'), 2))
    },
    {
        field: 'cfd_margin',
        label: 'Margen CFD',
        resolveValue: (stock) => firstText(stock?.cfdMarginLabel, formatMetricPercent(rawValue(stock, 'cfd_margin'), 2), rawValue(stock, 'cfd_margin'))
    },
    {
        field: 'resistance',
        label: 'Resist.',
        resolveValue: resolveResistanceLabel
    },
    {
        field: 'anual_var',
        label: 'Var. anual',
        resolveValue: (stock) => firstText(formatMetricPercent(rawValue(stock, 'tae'), 2), rawValue(stock, 'tae'))
    },
    {
        field: 'wave_number',
        label: 'Onda',
        resolveValue: (stock) => firstText(formatNumber(stock?.waveNumberLabel), stock?.waveNumberLabel, formatNumber(rawValue(stock, 'num_ona_actual')), rawValue(stock, 'num_ona_actual'))
    },
    {
        field: 'next_entry_lt',
        label: 'Sig. LT',
        resolveValue: (stock) => firstText(formatNumber(rawValue(stock, 'entrada_seg')), rawValue(stock, 'entrada_seg'))
    },
    {
        field: 'first_trading_date',
        label: '1a fecha',
        resolveValue: (stock) => firstText(stock?.firstBarDateFormatted, stock?.firstBarDateLabel, formatDateLabel(rawValue(stock, 'fecha_primera_barra')), rawValue(stock, 'fecha_primera_barra'))
    },
    {
        field: 'risk_reward',
        label: 'Inv/Riesgo',
        resolveValue: (stock) => firstText(stock?.riskRewardLabel, formatNumber(rawValue(stock, 'risk_reward')), rawValue(stock, 'risk_reward'))
    },
    {
        field: 'tick_size',
        label: 'Tick size',
        resolveValue: (stock) => firstText(formatNumber(rawValue(stock, 'tick_size')), rawValue(stock, 'tick_size'))
    },
    {
        field: 'user_platinum_compatibility',
        label: 'Compat.',
        resolveValue: formatCompatibility
    },
    {
        field: 'open_fee',
        label: 'Comis. ent.',
        resolveValue: (stock) => firstText(formatNumber(rawValue(stock, 'open_fee')), rawValue(stock, 'open_fee'))
    },
    {
        field: 'benefit',
        label: 'Beneficio',
        resolveValue: (stock) => firstText(formatNumber(rawValue(stock, 'benefit')), rawValue(stock, 'benefit'))
    },
    {
        field: 'insured_benefit',
        label: 'Benef. aseg.',
        resolveValue: (stock) => firstText(formatNumber(rawValue(stock, 'insured_benefit')), rawValue(stock, 'insured_benefit'))
    },
    {
        field: 'in_date',
        label: 'F. entrada',
        resolveValue: (stock) => firstText(formatDateLabel(rawValue(stock, 'in_date')), rawValue(stock, 'in_date'))
    }
];

const catalogByField = new Map(CHARTING_METRIC_CATALOG.map((item) => [item.field, item]));
const fixedDisplayFields = [...CHARTING_FIXED_METRIC_FIELDS].filter((field) => !CHARTING_IDENTITY_METRIC_FIELDS.has(field) && catalogByField.has(field));

const normalizeMetricRestrictionReason = (field, reason) => {
    const normalizedReason = toStringValue(reason).toLowerCase();

    if (normalizedReason.includes('silver')) {
        return 'Disponible a partir del plan Plata.';
    }

    if (normalizedReason.includes('gold')) {
        return 'Disponible a partir del plan Oro.';
    }

    return field ? 'Esta métrica no está disponible para tu plan.' : 'Métrica no disponible.';
};

export const normalizeChartingMetricField = (field) => {
    const normalizedField = toStringValue(field);

    return LEGACY_FIELD_ALIASES[normalizedField] || normalizedField;
};

const normalizePreferenceItems = (payload) =>
    toArray(payload)
        .map((item) => {
            if (typeof item === 'string') {
                return normalizeChartingMetricField(item);
            }

            return normalizeChartingMetricField(item?.field || item?.name || item?.sorter);
        })
        .filter(Boolean);

export const resolveChartingMetricValue = (metric, stock) => {
    const value = metric?.resolveValue?.(stock);
    const normalizedValue = toStringValue(value);

    return normalizedValue || EMPTY_METRIC_VALUE;
};

export function buildChartingMetricTableColumns({ catalog = CHARTING_METRIC_CATALOG, activeFields = [] } = {}) {
    const activeFieldSet = new Set(toArray(activeFields));

    return toArray(catalog)
        .filter((metric) => activeFieldSet.has(metric.field) && !CHARTING_IDENTITY_METRIC_FIELDS.has(metric.field))
        .map((metric) => ({
            field: metric.field,
            label: metric.label,
            resolveValue: metric.resolveValue,
            removable: !CHARTING_FIXED_METRIC_FIELDS.has(metric.field)
        }));
}

export function resolveChartingMetricTableCells(columns = [], stock = null) {
    return toArray(columns).map((column) => ({
        field: column.field,
        label: column.label,
        value: resolveChartingMetricValue(column, stock)
    }));
}

export function normalizeChartingMetricPreferences({ stockPreferences = [] } = {}) {
    const capabilityPayload = !Array.isArray(stockPreferences) && stockPreferences && typeof stockPreferences === 'object' ? stockPreferences : null;
    const preferenceState = {
        ...DEFAULT_CHARTING_METRIC_PREFERENCE_STATE
    };
    const activeFields = [];
    const storedFields = normalizePreferenceItems(capabilityPayload ? capabilityPayload.selected_fields : stockPreferences);
    const allowedFields = new Set(normalizePreferenceItems(capabilityPayload?.allowed_fields || []));
    const restrictedFields = new Map(
        Object.entries(capabilityPayload?.restricted_fields || {}).map(([field, reason]) => {
            const normalizedField = normalizeChartingMetricField(field);
            return [normalizedField, normalizeMetricRestrictionReason(normalizedField, reason)];
        })
    );
    const hasCapabilityContract = Boolean(capabilityPayload && Array.isArray(capabilityPayload.allowed_fields));

    fixedDisplayFields.forEach((field) => {
        preferenceState[field] = field;
        activeFields.push(field);
    });

    storedFields.forEach((field) => {
        if (!PERSISTABLE_METRIC_FIELDS.includes(field)) {
            return;
        }

        preferenceState[field] = field;

        if (!activeFields.includes(field) && !CHARTING_IDENTITY_METRIC_FIELDS.has(field) && catalogByField.has(field)) {
            activeFields.push(field);
        }
    });

    if (!storedFields.length && !activeFields.length) {
        Object.entries(DEFAULT_CHARTING_METRIC_PREFERENCE_STATE).forEach(([field, isActive]) => {
            if (isActive && !CHARTING_IDENTITY_METRIC_FIELDS.has(field) && catalogByField.has(field)) {
                activeFields.push(field);
            }
        });
    }

    return {
        activeFields,
        preferenceState,
        catalog: CHARTING_METRIC_CATALOG
            .filter((metric) => !hasCapabilityContract || CHARTING_FIXED_METRIC_FIELDS.has(metric.field) || allowedFields.has(metric.field) || restrictedFields.has(metric.field))
            .map((metric) => ({
                ...metric,
                restricted: restrictedFields.has(metric.field),
                restrictionReason: restrictedFields.get(metric.field) || ''
            }))
    };
}

export function buildChartingMetricPreferencePayload(activeFields = []) {
    const preferenceState = Object.fromEntries(PERSISTABLE_METRIC_FIELDS.map((field) => [field, 0]));

    CHARTING_IDENTITY_METRIC_FIELDS.forEach((field) => {
        preferenceState[field] = field;
    });

    CHARTING_FIXED_METRIC_FIELDS.forEach((field) => {
        preferenceState[field] = field;
    });

    toArray(activeFields)
        .map(normalizeChartingMetricField)
        .forEach((field) => {
            if (PERSISTABLE_METRIC_FIELDS.includes(field)) {
                preferenceState[field] = field;
            }
        });

    return preferenceState;
}
